import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  LayoutDashboard,
  FolderKanban,
  CheckSquare,
  BarChart3,
  MessageSquare,
  Users,
  ShieldAlert,
  KeyRound,
  ChevronLeft,
  ChevronRight,
  Video,
  Plus,
  X,
  Star,
  Briefcase,
  Building2,
  ChevronsUpDown,
  Check,
  Lock,
  Layers,
  Globe,
  Folder,
  Code,
  Megaphone,
  TrendingUp,
  Shield,
} from 'lucide-react';
import { useAuth } from '../../context/AuthContext.tsx';
import { useRealtime } from '../../context/RealtimeContext.tsx';
import { useTenant } from '../../context/TenantContext.tsx';
import { useChatUnread } from '../../context/ChatUnreadContext.tsx';
import { usePermissions } from '../../context/PermissionsContext.tsx';
import { CreateTenantModal } from '../tenant/CreateTenantModal.tsx';
import { Project } from '../../../shared/types.ts';

interface SidebarProps {
  currentView: string;
  onNavigate: (view: string) => void;
  isCollapsed: boolean;
  onToggleCollapse: () => void;
  isMobileOpen?: boolean;
  onCloseMobile?: () => void;
}

export const Sidebar: React.FC<SidebarProps> = ({
  currentView,
  onNavigate,
  isCollapsed,
  onToggleCollapse,
  isMobileOpen = false,
  onCloseMobile,
}) => {
  const { user, token } = useAuth();
  const { subscribe } = useRealtime();
  const { totalUnread: chatUnread } = useChatUnread();
  const { can } = usePermissions();
  const {
    currentTenant,
    tenants,
    currentSpace,
    spaces,
    switchTenant,
    switchSpace,
    reloadTenants,
    reloadSpaces,
  } = useTenant();

  const [activeProjects, setActiveProjects] = useState<Project[]>([]);
  const [isTenantDropdownOpen, setIsTenantDropdownOpen] = useState(false);
  const [isMobileTenantDropdownOpen, setIsMobileTenantDropdownOpen] = useState(false);
  const [isCreateTenantOpen, setIsCreateTenantOpen] = useState(false);

  const tenantDropdownRef = useRef<HTMLDivElement>(null);

  // Close dropdown on outside click
  useEffect(() => {
    const handleOutsideClick = (e: MouseEvent) => {
      if (tenantDropdownRef.current && !tenantDropdownRef.current.contains(e.target as Node)) {
        setIsTenantDropdownOpen(false);
      }
    };
    if (isTenantDropdownOpen) {
      document.addEventListener('mousedown', handleOutsideClick);
    }
    return () => {
      document.removeEventListener('mousedown', handleOutsideClick);
    };
  }, [isTenantDropdownOpen]);

  const fetchProjects = useCallback(async () => {
    if (!token) return;
    try {
      const headers: Record<string, string> = {
        Authorization: `Bearer ${token}`,
        ...(user?.id ? { 'x-demo-user-id': user.id } : {}),
      };
      if (currentTenant?.id) {
        headers['x-tenant-id'] = currentTenant.id;
      }
      if (currentSpace?.id) {
        headers['x-space-id'] = currentSpace.id;
      }

      const res = await fetch('/api/projects', { headers });
      if (res.ok) {
        const data = await res.json();
        const allBoards: Project[] = (data.projects || []).filter((p: Project) => !p.isArchived);
        allBoards.sort((a, b) => {
          if (a.isStarred && !b.isStarred) return -1;
          if (!a.isStarred && b.isStarred) return 1;
          return 0;
        });
        setActiveProjects(allBoards.slice(0, 12));
      }
    } catch (err) {
      // Failed to load
    }
  }, [token, user?.id, currentTenant?.id, currentSpace?.id]);

  useEffect(() => {
    fetchProjects();
  }, [fetchProjects, currentView, currentTenant?.id, currentSpace?.id]);

  useEffect(() => {
    const handleSync = () => {
      fetchProjects();
    };

    window.addEventListener('project-created', handleSync);
    window.addEventListener('project-updated', handleSync);
    window.addEventListener('task-created', handleSync);
    window.addEventListener('task-updated', handleSync);
    window.addEventListener('task-archived', handleSync);
    window.addEventListener('task-restored', handleSync);
    window.addEventListener('tenant-changed', handleSync);
    window.addEventListener('space-changed', handleSync);

    const unsubscribe = subscribe((event) => {
      if (
        event.type === 'project_created' ||
        event.type === 'project_updated' ||
        event.type === 'project_deleted' ||
        event.type === 'task_created' ||
        event.type === 'task_updated' ||
        event.type === 'task_archived' ||
        event.type === 'task_restored' ||
        event.type === 'tasks_imported'
      ) {
        fetchProjects();
      }
    });

    return () => {
      window.removeEventListener('project-created', handleSync);
      window.removeEventListener('project-updated', handleSync);
      window.removeEventListener('task-created', handleSync);
      window.removeEventListener('task-updated', handleSync);
      window.removeEventListener('task-archived', handleSync);
      window.removeEventListener('task-restored', handleSync);
      window.removeEventListener('tenant-changed', handleSync);
      window.removeEventListener('space-changed', handleSync);
      unsubscribe();
    };
  }, [fetchProjects, subscribe]);

  const isGuest = user?.role === 'guest';
  const navItems = [
    { id: 'dashboard', label: 'Workspace', icon: LayoutDashboard },
    ...(!isGuest ? [{ id: 'teams', label: 'Teams', icon: Layers }] : []),
    { id: 'boards', label: 'Boards', icon: FolderKanban },
    { id: 'projects', label: 'Projects', icon: Briefcase },
    { id: 'my-tasks', label: 'My Tasks', icon: CheckSquare },
    { id: 'workload', label: 'Workload', icon: BarChart3 },
    ...(!isGuest
      ? [
          { id: 'chat', label: 'Messages', icon: MessageSquare },
          { id: 'meetings', label: 'Meetings', icon: Video },
        ]
      : []),
  ];

  if (can('security.view')) {
    navItems.push({ id: 'audit', label: 'Security', icon: ShieldAlert });
  }
  // Who may do what; administrators change it, others with Security access can read it
  if (can('roles.manage') || can('security.view')) {
    navItems.push({ id: 'roles', label: 'Roles & Access', icon: KeyRound });
  }

  return (
    <>
      {/* Modal for Tenant creation */}
      <CreateTenantModal
        isOpen={isCreateTenantOpen}
        onClose={() => setIsCreateTenantOpen(false)}
        onSuccess={() => {
          reloadTenants();
          fetchProjects();
        }}
      />

      {/* Mobile Slide-Over Navigation Drawer */}
      <div
        className={`fixed inset-0 z-50 md:hidden transition-opacity duration-300 ${
          isMobileOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'
        }`}
        aria-hidden={!isMobileOpen}
      >
        {/* Backdrop */}
        <div
          className="fixed inset-0 bg-black/60 backdrop-blur-sm transition-opacity"
          onClick={onCloseMobile}
        />
        {/* Drawer Body */}
        <div
          className={`fixed top-0 bottom-0 left-0 w-80 max-w-[85vw] bg-[#eaecf0] dark:bg-[#0c0e12] pt-safe pb-safe shadow-2xl flex flex-col py-3 px-3 transform transition-transform duration-300 z-10 ${
            isMobileOpen ? 'translate-x-0' : '-translate-x-full'
          }`}
        >
          {/* Header */}
          <div className="h-11 flex items-center justify-between px-2 mb-2">
            <div
              onClick={() => {
                onNavigate('dashboard');
                onCloseMobile?.();
              }}
              className="flex items-center cursor-pointer"
            >
              <img
                src="/ihb-pulse-primary.png"
                alt="Pulse"
                className="h-7 max-w-[160px] object-contain dark:hidden"
              />
              <img
                src="/ihb-pulse-white.png"
                alt="Pulse"
                className="h-7 max-w-[160px] object-contain hidden dark:block"
              />
            </div>
            <button
              onClick={onCloseMobile}
              className="p-2 rounded-full text-[#667085] hover:text-[#121417] dark:hover:text-white hover:bg-white/80 dark:hover:bg-surface-800 transition-colors"
              aria-label="Close navigation menu"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Mobile Organization Selector */}
          <div className="relative px-1 mb-2">
            <button
              onClick={() => setIsMobileTenantDropdownOpen(!isMobileTenantDropdownOpen)}
              className="w-full flex items-center justify-between p-2 rounded-2xl bg-white/70 dark:bg-surface-850 hover:bg-white dark:hover:bg-surface-800 border border-white/60 dark:border-surface-750 shadow-neo-card transition-all"
            >
              <div className="flex items-center space-x-2.5 min-w-0">
                <div className="w-7 h-7 rounded-xl bg-gradient-to-tr from-brand-600 to-indigo-500 text-white flex items-center justify-center font-bold text-xs shadow-sm shrink-0">
                  {currentTenant?.name ? (
                    currentTenant.name.charAt(0).toUpperCase()
                  ) : (
                    <Building2 className="w-3.5 h-3.5" />
                  )}
                </div>
                <div className="text-left min-w-0">
                  <div className="text-xs font-bold text-surface-900 dark:text-surface-100 truncate">
                    {currentTenant?.name || 'IHB Digital'}
                  </div>
                  <div className="text-[10px] text-surface-500 dark:text-surface-400 capitalize">
                    {currentTenant?.role ? `${currentTenant.role} Organization` : 'Organization'}
                  </div>
                </div>
              </div>
              <ChevronsUpDown className="w-3.5 h-3.5 text-surface-400 shrink-0 ml-1" />
            </button>

            {isMobileTenantDropdownOpen && (
              <div className="mt-1 bg-white dark:bg-surface-900 rounded-2xl shadow-neo border border-surface-200 dark:border-surface-700 py-1.5 px-1">
                <div className="px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider text-surface-400">
                  Switch Organization
                </div>
                <div className="max-h-40 overflow-y-auto space-y-0.5">
                  {tenants.map((t) => {
                    const isSelected = currentTenant?.id === t.id;
                    return (
                      <button
                        key={t.id}
                        onClick={async () => {
                          setIsMobileTenantDropdownOpen(false);
                          await switchTenant(t.id);
                        }}
                        className={`w-full flex items-center justify-between px-2.5 py-1.5 rounded-xl text-xs transition-colors ${
                          isSelected
                            ? 'bg-brand-50 text-brand-700 dark:bg-brand-950/40 dark:text-brand-300 font-bold'
                            : 'text-surface-700 dark:text-surface-300 hover:bg-surface-100 dark:hover:bg-surface-800'
                        }`}
                      >
                        <span className="truncate">{t.name}</span>
                        {isSelected && (
                          <Check className="w-3.5 h-3.5 text-brand-600 dark:text-brand-400 shrink-0" />
                        )}
                      </button>
                    );
                  })}
                </div>
                <div className="pt-1.5 mt-1 border-t border-surface-150 dark:border-surface-800">
                  <button
                    onClick={() => {
                      setIsMobileTenantDropdownOpen(false);
                      setIsCreateTenantOpen(true);
                    }}
                    className="w-full flex items-center space-x-2 px-2.5 py-1.5 rounded-xl text-xs text-brand-600 dark:text-brand-400 hover:bg-brand-50/60 font-medium"
                  >
                    <Plus className="w-3.5 h-3.5" />
                    <span>Create Organization</span>
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Navigation Links for Mobile */}
          <div className="flex-1 overflow-y-auto space-y-1 pr-1 touch-scroll">
            {navItems.map((item) => {
              const Icon = item.icon;
              const isActive =
                item.id === 'boards'
                  ? currentView === 'boards' || currentView.startsWith('project-')
                  : currentView === item.id;
              return (
                <button
                  key={item.id}
                  onClick={() => {
                    onNavigate(item.id);
                    onCloseMobile?.();
                  }}
                  className={`w-full flex items-center rounded-2xl font-semibold text-xs h-10 px-3.5 space-x-3 transition-all ${
                    isActive
                      ? 'bg-[#121417] text-white dark:bg-[#f3f4f6] dark:text-[#121417] shadow-neo'
                      : 'text-[#475467] dark:text-[#9aa2b1] hover:bg-white/70 dark:hover:bg-surface-850 hover:text-[#121417] dark:hover:text-white'
                  }`}
                >
                  <div
                    className={`w-5 h-5 rounded-full flex items-center justify-center shrink-0 ${
                      isActive
                        ? 'text-brand-500 dark:text-brand-600'
                        : 'text-[#475467] dark:text-[#9aa2b1]'
                    }`}
                  >
                    <Icon className="w-4 h-4 stroke-[2]" />
                  </div>
                  <span className="tracking-tight text-sm">{item.label}</span>
                  {item.id === 'chat' && chatUnread > 0 && (
                    <span className="ml-auto min-w-[18px] h-[18px] px-1 rounded-full bg-rose-500 text-white text-[10px] font-extrabold flex items-center justify-center">
                      {chatUnread > 99 ? '99+' : chatUnread}
                    </span>
                  )}
                </button>
              );
            })}

            {/* Active Projects Quick Switcher on mobile */}
            <div className="pt-3 px-2">
              <div className="px-2 pb-1.5 text-[10px] font-bold uppercase tracking-wider text-[#9aa2b1] flex items-center justify-between">
                <span>Projects</span>
                <button
                  onClick={() => {
                    onNavigate('projects');
                    onCloseMobile?.();
                  }}
                  className="hover:text-[#121417] dark:hover:text-white font-semibold text-[10px]"
                >
                  view all
                </button>
              </div>
              <div className="space-y-1">
                {activeProjects.map((p) => {
                  const isCurrentProject = currentView === `project-${p.id}`;
                  return (
                    <button
                      key={p.id}
                      onClick={() => {
                        onNavigate(`project-${p.id}`);
                        onCloseMobile?.();
                      }}
                      className={`w-full flex items-center justify-between px-3 py-2 rounded-2xl text-xs transition-all ${
                        isCurrentProject
                          ? 'bg-white dark:bg-surface-800 text-[#121417] dark:text-[#f3f4f6] font-bold shadow-neo-card'
                          : 'text-[#667085] dark:text-[#9aa2b1] hover:bg-white/50 dark:hover:bg-surface-850'
                      }`}
                    >
                      <div className="flex items-center space-x-2 truncate">
                        <span
                          className="w-2.5 h-2.5 rounded-full shrink-0"
                          style={{ backgroundColor: p.color || '#0162fc' }}
                        />
                        <span className="truncate">{p.name}</span>
                        {p.isStarred && (
                          <Star className="w-3 h-3 fill-amber-400 text-amber-400 shrink-0" />
                        )}
                      </div>
                      {p.progressPercent !== undefined && (
                        <span className="text-[10px] font-mono text-[#9aa2b1] shrink-0">
                          {p.progressPercent}%
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Footer status pill in mobile drawer */}
          <div className="pt-2 pb-1 px-1 border-t border-[#d5d8de] dark:border-surface-800 flex flex-col items-center justify-center shrink-0">
            <div className="w-full flex items-center justify-between py-1.5 px-3 rounded-2xl bg-white/70 dark:bg-surface-850/80 shadow-neo-card border border-white/50 dark:border-surface-700/50">
              <div className="flex items-center space-x-2">
                <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
                <span className="text-[11px] font-bold text-surface-800 dark:text-surface-200">
                  Pulse
                </span>
              </div>
              <span className="text-[10px] font-bold text-brand-600 dark:text-brand-400">
                {currentTenant?.slug ? `@${currentTenant.slug}` : 'Multi-Tenant'}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Desktop Persistent Sidebar */}
      <aside
        className={`hidden md:flex flex-col bg-[#eaecf0] dark:bg-[#0c0e12] transition-all duration-300 z-40 select-none py-3 pl-3 pr-2 shrink-0 ${
          isCollapsed ? 'w-20' : 'w-64'
        }`}
      >
        {/* Brand & Primary Logo Header */}
        <div className="h-11 flex items-center justify-between px-2 mb-2 shrink-0">
          <div
            onClick={() => onNavigate('dashboard')}
            className="flex items-center cursor-pointer overflow-hidden group py-1"
          >
            {isCollapsed ? (
              <>
                <img
                  src="/ihb-pulse-icon-primary.png"
                  alt="Pulse"
                  className="w-9 h-9 object-contain shrink-0 transition-transform group-hover:scale-105 dark:hidden"
                />
                <img
                  src="/ihb-pulse-icon-white.png"
                  alt="Pulse"
                  className="w-9 h-9 object-contain shrink-0 transition-transform group-hover:scale-105 hidden dark:block"
                />
              </>
            ) : (
              <div className="flex items-center">
                <img
                  src="/ihb-pulse-primary.png"
                  alt="Pulse"
                  className="h-7 max-w-[160px] object-contain shrink-0 transition-transform group-hover:scale-102 dark:hidden"
                />
                <img
                  src="/ihb-pulse-white.png"
                  alt="Pulse"
                  className="h-7 max-w-[160px] object-contain shrink-0 transition-transform group-hover:scale-102 hidden dark:block"
                />
              </div>
            )}
          </div>

          {/* Collapse toggle */}
          <button
            onClick={onToggleCollapse}
            aria-label={isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            className="p-1.5 rounded-full text-[#667085] hover:text-[#121417] dark:hover:text-white hover:bg-white/80 dark:hover:bg-surface-800 transition-colors"
          >
            {isCollapsed ? <ChevronRight className="w-4 h-4" /> : <ChevronLeft className="w-4 h-4" />}
          </button>
        </div>

        {/* Organization / Tenant Switcher (L0: Tenant) */}
        {!isCollapsed ? (
          <div ref={tenantDropdownRef} className="relative mb-3 px-1 shrink-0">
            <button
              onClick={() => setIsTenantDropdownOpen(!isTenantDropdownOpen)}
              className="w-full flex items-center justify-between p-2 rounded-2xl bg-white/70 dark:bg-surface-850 hover:bg-white dark:hover:bg-surface-800 border border-white/60 dark:border-surface-750 shadow-neo-card transition-all group"
              aria-expanded={isTenantDropdownOpen}
              aria-label="Switch organization"
            >
              <div className="flex items-center space-x-2.5 min-w-0">
                <div className="w-7 h-7 rounded-xl bg-gradient-to-tr from-brand-600 to-indigo-500 text-white flex items-center justify-center font-bold text-xs shadow-sm shrink-0">
                  {currentTenant?.name ? (
                    currentTenant.name.charAt(0).toUpperCase()
                  ) : (
                    <Building2 className="w-3.5 h-3.5" />
                  )}
                </div>
                <div className="text-left min-w-0">
                  <div className="text-xs font-bold text-surface-900 dark:text-surface-100 truncate tracking-tight">
                    {currentTenant?.name || 'IHB Digital'}
                  </div>
                  <div className="text-[10px] text-surface-500 dark:text-surface-400 capitalize">
                    {currentTenant?.role ? `${currentTenant.role}` : 'Workspace'}
                  </div>
                </div>
              </div>
              <ChevronsUpDown className="w-3.5 h-3.5 text-surface-400 group-hover:text-surface-600 dark:group-hover:text-surface-300 shrink-0 ml-1" />
            </button>

            {/* Dropdown Menu */}
            {isTenantDropdownOpen && (
              <div className="absolute left-1 right-1 top-full mt-1.5 z-50 bg-white dark:bg-surface-900 rounded-2xl shadow-neo border border-surface-200 dark:border-surface-700 py-1.5 px-1 animate-in fade-in zoom-in-95 duration-150">
                <div className="px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider text-surface-400 dark:text-surface-500 flex items-center justify-between">
                  <span>Organizations</span>
                  <span className="text-[9px] font-mono font-normal">L0 Tier</span>
                </div>
                <div className="max-h-48 overflow-y-auto space-y-0.5">
                  {tenants.map((t) => {
                    const isSelected = currentTenant?.id === t.id;
                    return (
                      <button
                        key={t.id}
                        onClick={async () => {
                          setIsTenantDropdownOpen(false);
                          await switchTenant(t.id);
                        }}
                        className={`w-full flex items-center justify-between px-2.5 py-1.5 rounded-xl text-xs transition-colors ${
                          isSelected
                            ? 'bg-brand-50 text-brand-700 dark:bg-brand-950/40 dark:text-brand-300 font-bold'
                            : 'text-surface-700 dark:text-surface-300 hover:bg-surface-100 dark:hover:bg-surface-800'
                        }`}
                      >
                        <div className="flex items-center space-x-2 truncate">
                          <div className="w-5 h-5 rounded-lg bg-surface-200 dark:bg-surface-800 flex items-center justify-center text-[10px] font-bold text-surface-700 dark:text-surface-300 shrink-0">
                            {t.name.charAt(0).toUpperCase()}
                          </div>
                          <span className="truncate">{t.name}</span>
                        </div>
                        {isSelected && (
                          <Check className="w-3.5 h-3.5 text-brand-600 dark:text-brand-400 shrink-0 ml-1" />
                        )}
                      </button>
                    );
                  })}
                </div>
                <div className="pt-1.5 mt-1 border-t border-surface-150 dark:border-surface-800">
                  <button
                    onClick={() => {
                      setIsTenantDropdownOpen(false);
                      setIsCreateTenantOpen(true);
                    }}
                    className="w-full flex items-center space-x-2 px-2.5 py-1.5 rounded-xl text-xs text-brand-600 dark:text-brand-400 hover:bg-brand-50/60 dark:hover:bg-brand-950/30 font-medium transition-colors"
                  >
                    <Plus className="w-3.5 h-3.5" />
                    <span>Create Organization</span>
                  </button>
                </div>
              </div>
            )}
          </div>
        ) : (
          <div ref={tenantDropdownRef} className="relative mb-3 flex justify-center shrink-0">
            <button
              onClick={() => setIsTenantDropdownOpen(!isTenantDropdownOpen)}
              title={currentTenant?.name || 'Switch Organization'}
              className="w-9 h-9 rounded-2xl bg-gradient-to-tr from-brand-600 to-indigo-500 text-white flex items-center justify-center font-bold text-sm shadow-sm hover:scale-105 transition-all"
            >
              {currentTenant?.name ? (
                currentTenant.name.charAt(0).toUpperCase()
              ) : (
                <Building2 className="w-4 h-4" />
              )}
            </button>

            {isTenantDropdownOpen && (
              <div className="absolute left-full top-0 ml-2 w-56 z-50 bg-white dark:bg-surface-900 rounded-2xl shadow-neo border border-surface-200 dark:border-surface-700 py-1.5 px-1 animate-in fade-in zoom-in-95 duration-150">
                <div className="px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider text-surface-400">
                  Organizations
                </div>
                <div className="max-h-48 overflow-y-auto space-y-0.5">
                  {tenants.map((t) => {
                    const isSelected = currentTenant?.id === t.id;
                    return (
                      <button
                        key={t.id}
                        onClick={async () => {
                          setIsTenantDropdownOpen(false);
                          await switchTenant(t.id);
                        }}
                        className={`w-full flex items-center justify-between px-2.5 py-1.5 rounded-xl text-xs transition-colors ${
                          isSelected
                            ? 'bg-brand-50 text-brand-700 dark:bg-brand-950/40 dark:text-brand-300 font-bold'
                            : 'text-surface-700 dark:text-surface-300 hover:bg-surface-100 dark:hover:bg-surface-800'
                        }`}
                      >
                        <span className="truncate">{t.name}</span>
                        {isSelected && (
                          <Check className="w-3.5 h-3.5 text-brand-600 dark:text-brand-400 shrink-0" />
                        )}
                      </button>
                    );
                  })}
                </div>
                <div className="pt-1.5 mt-1 border-t border-surface-150 dark:border-surface-800">
                  <button
                    onClick={() => {
                      setIsTenantDropdownOpen(false);
                      setIsCreateTenantOpen(true);
                    }}
                    className="w-full flex items-center space-x-2 px-2.5 py-1.5 rounded-xl text-xs text-brand-600 dark:text-brand-400 hover:bg-brand-50/60 font-medium"
                  >
                    <Plus className="w-3.5 h-3.5" />
                    <span>Create Organization</span>
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Floating Pill Navigation Container */}
        <div data-tour="sidebar-nav" className="flex-1 overflow-y-auto space-y-1 pr-1">
          {navItems.map((item) => {
            const Icon = item.icon;
            const isActive =
              item.id === 'boards'
                ? currentView === 'boards' || currentView.startsWith('project-')
                : item.id === 'teams'
                ? currentView === 'teams' || currentView.startsWith('team-')
                : currentView === item.id;
            return (
              <button
                key={item.id}
                onClick={() => onNavigate(item.id)}
                title={isCollapsed ? item.label : undefined}
                className={`relative w-full flex items-center rounded-full font-semibold text-xs transition-all duration-200 ${
                  isActive
                    ? 'bg-[#121417] text-white dark:bg-[#f3f4f6] dark:text-[#121417] shadow-neo'
                    : 'text-[#475467] dark:text-[#9aa2b1] hover:bg-white/70 dark:hover:bg-surface-850 hover:text-[#121417] dark:hover:text-white'
                } ${isCollapsed ? 'h-9 justify-center' : 'h-9 px-3.5 space-x-2.5'}`}
              >
                <div
                  className={`w-5 h-5 rounded-full flex items-center justify-center shrink-0 ${
                    isActive
                      ? 'text-brand-500 dark:text-brand-600'
                      : 'text-[#475467] dark:text-[#9aa2b1]'
                  }`}
                >
                  <Icon className="w-4 h-4 stroke-[2]" />
                </div>
                {!isCollapsed && <span className="tracking-tight">{item.label}</span>}
                {item.id === 'chat' && chatUnread > 0 && (
                  <span
                    className={`min-w-[18px] h-[18px] px-1 rounded-full bg-rose-500 text-white text-[10px] font-extrabold flex items-center justify-center ${
                      isCollapsed ? 'absolute -top-0.5 -right-0.5' : 'ml-auto'
                    }`}
                    aria-label={`${chatUnread} unread messages`}
                  >
                    {chatUnread > 99 ? '99+' : chatUnread}
                  </span>
                )}
              </button>
            );
          })}

          {/* Active Projects Quick Switcher */}
          {!isCollapsed && (
            <div className="pt-3 px-2">
              <div className="px-2 pb-1.5 text-[10px] font-bold uppercase tracking-wider text-[#9aa2b1] flex items-center justify-between">
                <span>Active Boards</span>
                <div className="flex items-center space-x-2">
                  <button
                    onClick={() => onNavigate('projects')}
                    className="hover:text-[#121417] dark:hover:text-white font-semibold text-[10px]"
                  >
                    all projects
                  </button>
                </div>
              </div>
              <div className="space-y-1">
                {activeProjects.map((p) => {
                  const isCurrentProject = currentView === `project-${p.id}`;
                  return (
                    <button
                      key={p.id}
                      onClick={() => onNavigate(`project-${p.id}`)}
                      className={`w-full flex items-center justify-between px-3 py-1.5 rounded-2xl text-xs transition-all ${
                        isCurrentProject
                          ? 'bg-white dark:bg-surface-800 text-[#121417] dark:text-[#f3f4f6] font-bold shadow-neo-card'
                          : 'text-[#667085] dark:text-[#9aa2b1] hover:bg-white/50 dark:hover:bg-surface-850'
                      }`}
                    >
                      <div className="flex items-center space-x-2 truncate">
                        <span
                          className="w-2.5 h-2.5 rounded-full shrink-0"
                          style={{ backgroundColor: p.color || '#0162fc' }}
                        />
                        <span className="truncate">{p.name}</span>
                        {p.isStarred && (
                          <Star className="w-3 h-3 fill-amber-400 text-amber-400 shrink-0" />
                        )}
                      </div>
                      {p.progressPercent !== undefined && (
                        <span className="text-[10px] font-mono text-[#9aa2b1] shrink-0">
                          {p.progressPercent}%
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>
            </div>
          )}
        </div>

        {/* Powered by Pulse Footer */}
        <div className="pt-2 pb-1 px-1 border-t border-[#d5d8de] dark:border-surface-800 flex flex-col items-center justify-center shrink-0">
          {!isCollapsed ? (
            <div className="w-full flex items-center justify-between py-1.5 px-3 rounded-2xl bg-white/70 dark:bg-surface-850/80 shadow-neo-card border border-white/50 dark:border-surface-700/50">
              <div className="flex items-center space-x-2">
                <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
                <span className="text-[11px] font-bold text-surface-800 dark:text-surface-200">
                  Pulse
                </span>
              </div>
              <span className="text-[10px] font-bold text-brand-600 dark:text-brand-400">
                {currentTenant?.slug ? `@${currentTenant.slug}` : 'Multi-Tenant'}
              </span>
            </div>
          ) : (
            <div
              className="w-9 h-9 rounded-2xl bg-white/70 dark:bg-surface-850/80 flex items-center justify-center shadow-neo-card border border-white/50 dark:border-surface-700/50"
              title={`Pulse • ${currentTenant?.name || 'Active'}`}
            >
              <span className="w-2.5 h-2.5 rounded-full bg-emerald-500 animate-pulse" />
            </div>
          )}
        </div>
      </aside>
    </>
  );
};
